import { merge } from "lodash";
import moment from "moment-timezone";

import "@mobiscroll/javascript/dist/css/mobiscroll.min.css";
import { breakpoints } from "@/mixins";
import {
  EventAPIResponse,
  ResourceAPIResponse,
  TimelineEvent,
  TimelineResource
} from "@/typings/scheduler";
import {
  eventcalendar,
  MbscCalendarEvent,
  MbscEventcalendarOptions,
  MbscEventcalendarView,
  momentTimezone
} from "@mobiscroll/javascript";
import { draggable, Eventcalendar } from "@mobiscroll/javascript";

// use moment-timezone for timezone https://mobiscroll.com/docs/javascript/eventcalendar/timezones#the-moment-timezone-library
momentTimezone.moment = moment;

/**
 * The Sera EventCalendar component.
 *
 * Usage:
 *
 * ```
 * new EventCalendar({ node: HTMLElement; options: MbscEventcalendarOptions });
 * ```
 */
export default class EventCalendar extends Eventcalendar {
  node: HTMLElement;
  desktopViewSettings: MbscEventcalendarView = {
    timeline: {
      maxEventStack: 4,
      resolutionHorizontal: "hour",
      type: "day",
      resourceReorder: false,
      zoomLevels: {
        25: {
          // see all hours and calls
          type: "day",
          size: 1,
          resolutionHorizontal: "hour",
          timeCellStep: 180,
          timeLabelStep: 60
        },
        50: {
          type: "day",
          size: 1,
          resolutionHorizontal: "hour",
          timeCellStep: 60,
          timeLabelStep: 60
        },
        75: {
          type: "day",
          size: 1,
          resolutionHorizontal: "hour",
          timeCellStep: 30,
          timeLabelStep: 60
        },
        100: {
          // see 2-4 hours
          type: "day",
          size: 1,
          resolutionHorizontal: "hour",
          timeCellStep: 10,
          timeLabelStep: 60
        }
      }
    }
  };
  mobileViewSettings: MbscEventcalendarView = {
    schedule: {
      maxEventStack: 4,
      type: "day"
    }
  };
  mobileOptions: MbscEventcalendarOptions = {
    externalDrop: false,
    dragBetweenResources: false,
    dragInTime: false,
    dragToResize: false,
    dragToCreate: false,
    dragToMove: false
  };
  options: MbscEventcalendarOptions = {
    timezonePlugin: momentTimezone,
    dataTimezone: window.currentTenant.timezone,
    displayTimezone: window.currentTenant.timezone,
    theme: "ios",
    themeVariant: "light",
    groupBy: "resource",
    resources: [],
    data: [],
    renderHeader: this.renderHeader,
    renderDay: () => {},
    zoomLevel: 50,
    externalDrop: true,
    dragBetweenResources: true,
    dragInTime: true,
    dragTimeStep: 30,
    dragToResize: true,
    dragToCreate: true,
    dragToMove: true,
    eventOrder: this.orderEvents,
    colors: [
      {
        start: moment().startOf("year").startOf("day"),
        end: moment().startOf("year").startOf("day").add("hours", 6),
        background: "#f9fafb",
        recurring: {
          repeat: "daily"
        }
      },
      {
        start: moment().startOf("year").endOf("day").subtract("hours", 6),
        end: moment().startOf("year").endOf("day"),
        background: "#f9fafb",
        recurring: {
          repeat: "daily"
        }
      }
    ]
  };
  instance: Eventcalendar | { [key: string]: Eventcalendar };

  constructor(params: {
    node: HTMLElement;
    options?: MbscEventcalendarOptions;
  }) {
    super();
    this.node = params.node;
    this.options = merge({}, this.options, params.options);
    let viewSettings = this.desktopViewSettings;
    if (breakpoints().sm) {
      viewSettings = this.mobileViewSettings;
      this.options = merge({}, this.options, this.mobileOptions);
    }
    this.options.view = merge({}, this.options.view, viewSettings);
    if (!this.node) return;
    this.mount();
  }

  mount() {
    this.instance = eventcalendar(this.node, this.options);
  }

  renderHeader() {
    return "";
  }

  navigate(date) {
    this.instance.navigate(date, true);
  }

  orderEvents(event1: MbscCalendarEvent, event2: MbscCalendarEvent) {
    // Event order:
    // 1. Start Time
    // 2. High priority
    // 3. Alphabetical by title
    if (moment(event1.start).isAfter(event2.start)) return 1;
    if (event1.details.appointment?.priority === "high") return -1;
    return -1;
  }

  setOptions(options: MbscEventcalendarOptions) {
    this.instance.setOptions(options);
  }

  setEvents(data: EventAPIResponse[]) {
    const events = data.reduce((events: TimelineEvent[], event) => {
      if (event.appointment) {
        events.push(
          new TimelineEvent({
            id: event.id,
            title: this.getEventTitle(event),
            start: this.getEventStart(event),
            end: this.getEventEnd(event),
            resource: event.technician?.id || -1,
            ghost: Boolean(event.is_ghost),
            editable: event.appointment.status === "pending",
            details: event
          })
        );
      } else {
        event.event_details?.technicians?.forEach((tech) => {
          events.push(
            new TimelineEvent({
              id: event.event_details?.id || -1,
              title: event.event_details?.name || "",
              start: event.event_details?.starts_at || "",
              end: event.event_details?.ends_at || "",
              resource: tech.id,
              ghost: false,
              editable: false,
              details: event
            })
          );
        });
      }
      return events;
    }, []);

    return this.instance.setEvents(events);
  }

  setResources(data: ResourceAPIResponse[]) {
    this.instance.setOptions({
      resources: this.buildResources(data)
    });
  }

  destroy(): void {
    this.instance.destroy();
  }

  draggable(
    selector: string,
    options: { dragData: { [k: string]: any } }
  ): void {
    draggable(selector, options);
  }

  buildResources(data: ResourceAPIResponse[]) {
    if (breakpoints().sm) {
      return data.map(
        (tech) =>
          new TimelineResource({
            id: tech.id,
            color: "",
            name: tech.full_name
          })
      );
    }
    const groupMap: Record<string, ResourceAPIResponse[]> = {};
    const nonGrouped: ResourceAPIResponse[] = [];
    // Group items
    for (const resource of data) {
      if (!resource.department?.name) {
        nonGrouped.push(resource);
        continue;
      }
      if (!groupMap[resource.department.name]) {
        groupMap[resource.department.name] = [];
      }
      groupMap[resource.department.name].push(resource);
    }

    type Group = { name: string; children: TimelineResource[] };
    // Convert to array format with children
    let groups: (Group | TimelineResource)[] = Object.entries(groupMap)
      .sort()
      .map(([group, children], i) => ({
        id: group + i,
        name: group,
        eventCreation: false,
        children: children.map(
          (tech) =>
            new TimelineResource({
              id: tech.id,
              color: "",
              name: tech.full_name
            })
        )
      }));

    groups = nonGrouped
      .map(
        (tech) =>
          new TimelineResource({
            id: tech.id,
            color: "",
            name: tech.full_name,
            cssClass: tech.id === -1 ? `unassigned-resource-row` : ""
          })
      )
      .concat(groups);

    return groups;
  }

  getEventStart(event: EventAPIResponse) {
    if (!event.appointment) return "";
    if (event.is_ghost && event.appointment.ghost_scheduled_time)
      return event.appointment.ghost_scheduled_time;
    return (
      event.appointment.pre_work_start_time ||
      event.appointment.travel_start_time ||
      event.appointment.work_start_time ||
      event.appointment.arrival_window_start_time
    );
  }

  getEventEnd(event: EventAPIResponse) {
    if (!event.appointment) return "";
    // Show default size for 0 minute appointments
    if (event.appointment.estimated_duration_minutes === 0) {
      const start = this.getEventStart(event);
      return moment(start).add(15, "minutes").toISOString();
    }
    if (event.is_ghost && event.appointment.ghost_scheduled_time)
      return moment(event.appointment.ghost_scheduled_time).add(
        event.appointment.estimated_duration_minutes,
        "minutes"
      );
    switch (event.appointment.status) {
      // When traveling or working, return current time as end time
      case "accepted":
      case "running": {
        return moment(event.appointment.estimated_end_time).isSameOrAfter(
          new Date()
        )
          ? event.appointment.estimated_end_time
          : new Date();
      }
      default:
        return (
          event.appointment.work_end_time ||
          event.appointment.estimated_end_time ||
          event.appointment.arrival_window_end_time
        );
    }
  }

  getEventTitle(event: EventAPIResponse) {
    if (event.appointment) {
      const { service_category_name, customer } = event.appointment;
      return `${customer?.full_name} - ${service_category_name}`;
    }
    return event.event_details?.name || "";
  }
}
